import { Component } from 'react';
import PropTypes from 'prop-types';
import { parseFullName } from 'parse-full-name';
import EditStudentEditControl from './EditStudentEditControl';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import TextButton from '../../components/TextButton/TextButton';
import { isUserHasKey } from '../../GlobalFunctions';
import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import studentStyles from '../../SCSS/Students.module.scss';
import { Dialog, DialogContent } from '../../components/Dialog';

type Props = {
  student: StudentProxyType | { sid: string; userId: string };
  readingLevels: string[];
};

export class EditStudentModal extends Component<Props> {
  constructor(props: Props) {
    super(props);

    this.state = {
      student: undefined,
    };

    this.handleOnDone = this.handleOnDone.bind(this);
    this.handleOnSubmit = this.handleOnSubmit.bind(this);
    this.handleOnHide = this.handleOnHide.bind(this);
  }

  handleOnDone(newName) {
    this.handleOnSubmit(newName);
  }

  handleOnHide() {
    this.props.onHide();
  }

  async handleOnSubmit(values: { name: string; readingLevel: number; email?: string }) {
    const { name, readingLevel, email } = values;
    const newStudentObject = { ...this.state.student };

    if (name) {
      const parsedName = parseFullName(name);
      if (!parsedName.first.length && parsedName.last.length) {
        newStudentObject.firstName = parsedName.last;
        delete newStudentObject.lastName;
      } else if (parsedName.first.length && parsedName.last.length) {
        newStudentObject.firstName = parsedName.first;
        newStudentObject.lastName = parsedName.last;
      } else if (parsedName.first.length) {
        newStudentObject.firstName = parsedName.first;
      }

      if (parsedName.middle.length) {
        newStudentObject.middleName = parsedName.middle;
      } else {
        delete newStudentObject.middleName;
      }
    }

    if (readingLevel) {
      newStudentObject.readingLevel = readingLevel;
    }

    if (email !== undefined) {
      if (email && email.trim() !== '') {
        newStudentObject.email = email.trim();
      } else {
        delete newStudentObject.email;
      }
    }

    this.setState({
      student: newStudentObject,
    });

    await this.props.onReplaceStudent(this.props.indexOfStudentUnderEdit, newStudentObject);
    this.handleOnHide();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      this.state.student === undefined ||
      nextProps.student.sid !== this.state.student.sid ||
      nextProps.student.userId !== this.state.student.userId
    ) {
      this.setState({
        student: nextProps.student,
      });
    }
  }

  render() {
    return isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') ? (
      <Dialog
        open={this.props.show}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            this.handleOnHide();
          }
        }}
      >
        <DialogContent
          className='w-[min(95vw,680px)] rounded-[0.6rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
          overlayClassName='bg-black/50'
        >
          <div className='relative border-b-0 px-[2.6rem] pb-[1.2rem] pt-[2.4rem]'>
            <button
              type='button'
              onClick={this.handleOnHide}
              aria-label='Close'
              className='absolute right-[1rem] top-[0.7rem] border-0 bg-transparent p-0 text-[2.4rem] leading-none text-[#242424]'
            >
              x
            </button>
            <p className={studentStyles.ModalDialogHeader}>Edit Student</p>
          </div>
          <div className='px-[2.6rem]'>
            <EditStudentEditControl
              onSubmit={this.handleOnSubmit}
              student={this.state.student}
              readingLevels={this.props.readingLevels}
            />
          </div>
          <div className={studentStyles.ModalDialogFooter}>
            <TextButton uppercase grey bold underlineHover onClick={this.handleOnHide}>
              Or Cancel
            </TextButton>
          </div>
        </DialogContent>
      </Dialog>
    ) : (
      <Dialog
        open={this.props.show}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            this.handleOnHide();
          }
        }}
      >
        <DialogContent
          className='w-[min(95vw,680px)] rounded-[0.6rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
          overlayClassName='bg-black/50'
        >
          <div className='relative border-b-0 px-[2.6rem] pb-[1.2rem] pt-[2.4rem]'>
            <button
              type='button'
              onClick={this.handleOnHide}
              aria-label='Close'
              className='absolute right-[1rem] top-[0.7rem] border-0 bg-transparent p-0 text-[2.4rem] leading-none text-[#242424]'
            >
              x
            </button>
            <p className={studentStyles.ModalDialogHeader}>Edit Student</p>
          </div>
          <div className='px-[2.6rem]'>
            <EditStudentEditControl onSubmit={this.handleOnSubmit} student={this.props.student} />
          </div>
          <div className={studentStyles.ModalDialogFooter}>
            <form>
              <div className={studentStyles.ModalDialogFooter}>
                <PrimaryButton onClick={this.handleOnDone}>Done Editing Student</PrimaryButton>
              </div>
            </form>
            <TextButton uppercase grey bold underlineHover onClick={this.handleOnHide}>
              Or Cancel
            </TextButton>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

EditStudentModal.propTypes = {
  indexOfStudentUnderEdit: PropTypes.number,
  onHide: PropTypes.func.isRequired,
  onReplaceStudent: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  student: PropTypes.shape({
    firstName: PropTypes.string,
    sid: PropTypes.string,
    userId: PropTypes.string,
  }),
};
